import React from 'react';
import { inputCls } from './FormKit';
import { firmToday } from '../../utils/dateRange';

// Financial-Year-End picker — a date input plus month-end quick picks. Shared by
// every surface that asks for an FYE (Management engagements & QC records,
// Engagement Registration, Engagement Amendment, QCA).
//
// Lives here (not in a page module) because the shared engagement
// field set (EngagementFormFields.js) needs it, and importing a control out of a
// page module makes components/ depend on pages/ — a cycle waiting to happen.
//
// Year End almost always falls on a month end, so the quick picks offer the
// upcoming quarter ends; the calendar covers any other day.
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const QUARTER_MONTHS = [3, 6, 9, 12];
const lastDayOfMonth = (year, month) => new Date(year, month, 0).getDate(); // month: 1-12
const monthEndStr = (year, month) =>
  `${year}-${String(month).padStart(2, '0')}-${String(lastDayOfMonth(year, month)).padStart(2, '0')}`;

// Six FYE quick picks counting forward from today: the next
// six quarter ends on/after today, oldest → newest. Past quarter ends dropped
// out of the shortcut row — an FYE being registered is almost always ahead of
// today, and a back-dated one is typed into the calendar.
export const buildFyeQuickPicks = (today, currentYear) => {
  const all = [];
  for (let y = currentYear; y <= currentYear + 2; y++) {
    for (const month of QUARTER_MONTHS) all.push({ year: y, month, date: monthEndStr(y, month) });
  }
  all.sort((a, b) => (a.date < b.date ? -1 : 1));
  return all
    .filter((q) => q.date >= today) // today itself counts when it is a quarter end
    .slice(0, 6)
    .map((q) => ({
      ...q,
      label: `${lastDayOfMonth(q.year, q.month)} ${MONTH_LABELS[q.month - 1]} ${q.year}`,
    }));
};

// The date input comes FIRST and the quick picks sit under it as a lighter
// shortcut row: when the field shares a two-column grid with
// another date (Eng Reg's Date of Appointment) a leading chip cluster pushes the
// real control out of line with its neighbour and reads as the primary input.
// No "Quick pick" caption above the chips: a row of dates
// under a date input reads as shortcuts without being told so. Same on all five
// surfaces — this control is shared and must look identical everywhere.
export const FyeField = ({ value, onChange }) => {
  const currentYear = new Date().getFullYear();
  const today = firmToday();
  const quickPicks = buildFyeQuickPicks(today, currentYear);

  return (
    <div>
      <input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputCls} />
      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        {quickPicks.map((q) => {
          const selected = value === q.date;
          return (
            <button
              key={q.date}
              type="button"
              onClick={() => onChange(q.date)}
              className={`px-2 py-0.5 text-[11px] font-medium border rounded-full whitespace-nowrap ${
                selected
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700'
              }`}
            >
              {q.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FyeField;
